import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/Stat";
import { LeadLinkQR } from "@/components/LeadLinkQR";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Target, HandCoins, Sparkles, CalendarClock, FileWarning, FileText, CheckCircle2, Banknote, UsersRound, Bell, Link2 } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [venue, setVenue] = useState("all");
  const [venues, setVenues] = useState([]);
  const isAdmin = user.role === "admin";
  const shareLink = `${window.location.origin}/apply/${user.public_token}`;
  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Invitation link copied — share it with customers!"); };

  useEffect(() => { if (isAdmin) api.get("/venues").then((r) => setVenues(r.data)); }, [isAdmin]);

  useEffect(() => {
    const params = venue !== "all" ? { venue } : {};
    api.get("/dashboard/admin", { params }).then((r) => setS(r.data));
    api.get("/analytics/overdue-by-rm", { params }).then((r) => setOverdue(r.data));
  }, [venue]);

  if (!s) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      <PageHeader title="Command Centre" subtitle="Live performance across your entire loan operation."
        actions={
          <div className="flex flex-wrap gap-2 items-center">
            {isAdmin && venues.length > 0 && (
              <Select value={venue} onValueChange={setVenue}>
                <SelectTrigger className="w-48" data-testid="dashboard-venue-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <NotificationBell />
            <Button variant="outline" data-testid="admin-copy-invite" onClick={copyLink}><Link2 size={16} className="mr-2" />Share Invitation</Button>
            <LeadLinkQR url={shareLink} name="Admin Invite" label="QR" testid="admin-qr-btn" />
          </div>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <StatCard testid="admin-total-customers" label="Total Customers" value={s.total_customers} icon={Users} accent="blue" />
        <StatCard testid="admin-active-leads" label="Active Leads" value={s.active_leads} icon={Target} accent="amber" />
        <StatCard testid="admin-loan-interested" label="Loan Interested" value={s.loan_interested} icon={HandCoins} accent="green" />
        <StatCard testid="admin-mela" label="Mela Invited / Attended" value={`${s.mela_invited} / ${s.mela_attended}`} icon={Sparkles} accent="violet" />
        <StatCard testid="admin-followup-due" label="Follow-up Due" value={s.followup_due} icon={CalendarClock} accent="rose" />
        <StatCard testid="admin-docs-pending" label="Docs Pending" value={s.docs_pending} icon={FileWarning} accent="amber" />
        <StatCard testid="admin-applications" label="Applications" value={s.applications} icon={FileText} accent="blue" />
        <StatCard testid="admin-approved" label="Approved" value={s.approved} icon={CheckCircle2} accent="green" />
        <StatCard testid="admin-disbursed" label="Disbursed" value={s.disbursed} sub={inr(s.disbursed_amount)} icon={Banknote} accent="green" />
        <StatCard testid="admin-total-rms" label="Active RMs" value={s.total_rms} icon={UsersRound} accent="slate" />
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-bold text-slate-700 flex items-center gap-2"><Bell size={16} />Overdue-by-RM Nudges</div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/analytics")}>View analytics →</Button>
        </div>
        {overdue.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No overdue follow-ups across the team. 🎉</div>
        ) : (
          <div className="space-y-2">
            {overdue.map((o) => (
              <div key={o.username} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100" data-testid={`nudge-${o.username}`}>
                <div className="w-8 h-8 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center font-bold text-sm">{o.rm[0]}</div>
                <div className="flex-1"><span className="font-semibold text-slate-800">{o.rm}</span> has <b className="text-amber-700">{o.overdue}</b> overdue follow-ups</div>
                <Button size="sm" variant="outline" onClick={() => toast.success(`Nudge sent to ${o.rm}`)} data-testid={`send-nudge-${o.username}`}>Send Nudge</Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
